import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "ultimate-guide-secret-santa-large-groups",
  "title": "The Ultimate Guide to Organizing Secret Santa for Large Groups",
  "excerpt": "Planning a gift exchange for 50+ people? Learn proven strategies for managing exclusions, setting budgets, and ensuring everyone has a magical experience.",
  "date": "2024-12-01",
  "readTime": "8 min read",
  "category": "Guides",
  "image": "🎅",
  "heroImage": "https://images.unsplash.com/photo-1512909006721-3d6018887383?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# The Ultimate Guide to Organizing Secret Santa for Large Groups\n\nOrganizing a Secret Santa for a large group can feel overwhelming, but with the right approach and tools, it can be a smooth and enjoyable experience for everyone involved. Whether you're planning for your office, extended family, or community group, this comprehensive guide will walk you through every step.\n\n## Why Large Groups Need Special Planning\n\nWhen you're dealing with 50 or more participants, the complexity grows exponentially. You're not just matching names – you're managing:\n\n- Complex exclusion rules (couples, family members, previous year matches)\n- Multiple budget considerations across different income levels\n- Communication challenges with dozens of people\n- Technical logistics of the actual drawing\n- Tracking participation and ensuring everyone follows through\n\n## Step 1: Set Clear Expectations Early\n\n**Start Planning Early**: Begin your planning at least 4-6 weeks before your event. This gives people time to:\n\n- Confirm their participation\n- Plan their budgets\n- Shop thoughtfully rather than rushing\n\n**Communicate Clearly**: Send out a detailed initial email or announcement that includes:\n\n- Event date and location (if applicable)\n- Budget range and why it was chosen\n- Registration deadline\n- Rules about exclusions\n- Whether wishlists will be used\n\n## Step 2: Choose the Right Budget\n\nFor large groups, budget setting requires extra care. Consider:\n\n**The $25-30 Range**: Works well for casual workplace exchanges or friend groups where people are participating in multiple gift exchanges.\n\n**The $50-75 Range**: Ideal for close-knit teams or family gatherings where you want meaningful gifts but not financial stress.\n\n**The $100+ Range**: Reserved for executive teams or special family traditions where higher-value gifts are the norm.\n\n**Pro Tip**: Poll your group before setting a final budget. You might be surprised by preferences, and buy-in increases when people have input.\n\n## Conclusion\n\nWith proper planning and the right tools, organizing a Secret Santa for a large group can be a rewarding experience that brings joy to everyone involved."
};

export default function UltimateGuideSecretSantaLargeGroupsPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
